import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Card, CardContent,
  IconButton, Chip, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, LinearProgress, Tabs, Tab, Alert,
  Snackbar, InputAdornment, Tooltip, Avatar, AvatarGroup, Badge
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  CheckCircle as CheckIcon, RadioButtonUnchecked as UncheckedIcon,
  PlayArrow as StartIcon, Flag as FlagIcon, CalendarToday as CalendarIcon,
  AccessTime as TimeIcon, Category as CategoryIcon, Person as PersonIcon,
  Search as SearchIcon, FilterList as FilterIcon, Assessment as StatsIcon,
  TrendingUp as TrendingIcon, Warning as WarningIcon, CheckCircleOutline as DoneIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

const PRIORITY_CONFIG = {
  low: { color: 'success', icon: '🟢', label: 'Low' },
  medium: { color: 'info', icon: '🔵', label: 'Medium' },
  high: { color: 'warning', icon: '🟠', label: 'High' },
  urgent: { color: 'error', icon: '🔴', label: 'Urgent' }
};

const STATUS_CONFIG = {
  todo: { color: 'default', icon: '📋', label: 'To Do' },
  in_progress: { color: 'primary', icon: '⚡', label: 'In Progress' },
  completed: { color: 'success', icon: '✅', label: 'Completed' },
  cancelled: { color: 'error', icon: '❌', label: 'Cancelled' }
};

function TodoApp() {
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    category: ''
  });

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'todo',
    category: '',
    dueDate: '',
    estimatedTime: '',
    notes: '',
    assignedTo: '',
    createdBy: user?.id || ''
  });

  useEffect(() => {
    fetchDropdowns();
    fetchUsers();
    fetchTodos();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchTodos();
    fetchStats();
  }, [filters]);

  const fetchDropdowns = async () => {
    try {
      const response = await axios.get(`${API_URL}/dropdowns`);
      setCategories(response.data.data.todoCategories || []);
    } catch (error) {
      console.error('Failed to fetch dropdowns:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTodos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
        userId: user?.id || '',
        userRole: user?.role || ''
      });

      const response = await axios.get(`${API_URL}/todos?${params}`);
      setTodos(response.data.data.todos);
    } catch (error) {
      showSnackbar('Failed to fetch todos', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams({
        userId: user?.id || ''
      });
      const response = await axios.get(`${API_URL}/todos/stats?${params}`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const handleOpenDialog = (todo = null) => {
    if (todo) {
      setSelectedTodo(todo);
      setFormData({
        title: todo.title,
        description: todo.description || '',
        priority: todo.priority,
        status: todo.status,
        category: todo.category || '',
        dueDate: todo.dueDate ? new Date(todo.dueDate).toISOString().split('T')[0] : '',
        estimatedTime: todo.estimatedTime || '',
        notes: todo.notes || '',
        assignedTo: todo.assignedTo || '',
        createdBy: user?.id || ''
      });
    } else {
      setSelectedTodo(null);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: 'todo',
        category: '',
        dueDate: '',
        estimatedTime: '',
        notes: '',
        assignedTo: '',
        createdBy: user?.id || ''
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        estimatedTime: formData.estimatedTime ? parseInt(formData.estimatedTime) : null,
        dueDate: formData.dueDate || null
      };

      if (selectedTodo) {
        await axios.put(`${API_URL}/todos/${selectedTodo.id}`, submitData);
        showSnackbar('Todo updated successfully');
      } else {
        await axios.post(`${API_URL}/todos`, submitData);
        showSnackbar('Todo created successfully');
      }

      setOpenDialog(false);
      fetchTodos();
      fetchStats();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save todo', 'error');
    }
  };

  const handleStatusChange = async (todoId, newStatus) => {
    try {
      await axios.put(`${API_URL}/todos/${todoId}`, { status: newStatus });
      showSnackbar('Status updated');
      fetchTodos();
      fetchStats();
    } catch (error) {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleDelete = async (todoId) => {
    if (window.confirm('Are you sure you want to delete this todo?')) {
      try {
        await axios.delete(`${API_URL}/todos/${todoId}`);
        showSnackbar('Todo deleted successfully');
        fetchTodos();
        fetchStats();
      } catch (error) {
        showSnackbar('Failed to delete todo', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() ;
  };

  const formatDate = (date) => {
    if (!date) return 'No due date';
    return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const groupedTodos = {
    todo: todos.filter(t => t.status === 'todo'),
    in_progress: todos.filter(t => t.status === 'in_progress'),
    completed: todos.filter(t => t.status === 'completed')
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          📋 Todo App
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="large"
        >
          New Task
        </Button>
      </Box>

      {/* Info Alert for privileged roles */}
      {user && ['owner', 'manager', 'hr'].includes(user.role) && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>👀 Full Access View:</strong> You can see all tasks in the system. 
          Completed tasks from the last 1 month are shown.
        </Alert>
      )}

      {/* Info Alert for regular users */}
      {user && !['owner', 'manager', 'hr'].includes(user.role) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>ℹ️ Viewing your assigned tasks.</strong> You can see tasks assigned to you or created by you. 
          Completed tasks from the last 1 month are shown.
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 2 }}>
        <Tab label="📋 Kanban Board" />
        <Tab label="✅ Completed" />
        <Tab label="📊 Statistics" />
      </Tabs>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tasks..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label="Priority"
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>{config.icon} {config.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label="Category"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>{config.icon} {config.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setFilters({ search: '', status: '', priority: '', category: '' })}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {currentTab === 0 && (
        <Grid container spacing={3}>
          {/* To Do Column */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', minHeight: 400 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  📋 To Do
                </Typography>
                <Chip label={groupedTodos.todo.length} color="default" size="small" />
              </Box>
              {groupedTodos.todo.map(todo => (
                <Card key={todo.id} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => {
                  setSelectedTodo(todo);
                  setOpenDetailsDialog(true);
                }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {todo.title}
                      </Typography>
                      <Chip
                        label={PRIORITY_CONFIG[todo.priority].label}
                        size="small"
                        color={PRIORITY_CONFIG[todo.priority].color}
                      />
                    </Box>
                    {todo.description && (
                      <Typography variant="body2" color="text.secondary" mb={1} noWrap>
                        {todo.description}
                      </Typography>
                    )}
                    <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                      {todo.category && (
                        <Chip label={todo.category} size="small" variant="outlined" />
                      )}
                      {todo.dueDate && (
                        <Chip
                          icon={<CalendarIcon />}
                          label={formatDate(todo.dueDate)}
                          size="small"
                          color={isOverdue(todo.dueDate) ? 'error' : 'default'}
                        />
                      )}
                    </Box>
                    <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Start">
                        <IconButton size="small" onClick={() => handleStatusChange(todo.id, 'in_progress')} color="primary">
                          <StartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDialog(todo); }} color="info">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(todo.id); }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {groupedTodos.todo.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">No pending tasks</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* In Progress Column */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: '#e3f2fd', minHeight: 400 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  ⚡ In Progress
                </Typography>
                <Chip label={groupedTodos.in_progress.length} color="primary" size="small" />
              </Box>
              {groupedTodos.in_progress.map(todo => (
                <Card key={todo.id} sx={{ mb: 2, cursor: 'pointer', borderLeft: '4px solid', borderColor: 'primary.main' }} onClick={() => {
                  setSelectedTodo(todo);
                  setOpenDetailsDialog(true);
                }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {todo.title}
                      </Typography>
                      <Chip
                        label={PRIORITY_CONFIG[todo.priority].label}
                        size="small"
                        color={PRIORITY_CONFIG[todo.priority].color}
                      />
                    </Box>
                    {todo.description && (
                      <Typography variant="body2" color="text.secondary" mb={1} noWrap>
                        {todo.description}
                      </Typography>
                    )}
                    {todo.progress > 0 && (
                      <Box mb={1}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption">Progress</Typography>
                          <Typography variant="caption">{todo.progress}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={todo.progress} />
                      </Box>
                    )}
                    <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                      {todo.category && (
                        <Chip label={todo.category} size="small" variant="outlined" />
                      )}
                      {todo.dueDate && (
                        <Chip
                          icon={<CalendarIcon />}
                          label={formatDate(todo.dueDate)}
                          size="small"
                          color={isOverdue(todo.dueDate) ? 'error' : 'default'}
                        />
                      )}
                    </Box>
                    <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Complete">
                        <IconButton size="small" onClick={() => handleStatusChange(todo.id, 'completed')} color="success">
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDialog(todo); }} color="info">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(todo.id); }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {groupedTodos.in_progress.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">No tasks in progress</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

        </Grid>
      )}

      {currentTab === 1 && (
        <Box>
          {groupedTodos.completed.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No completed tasks yet</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {groupedTodos.completed.map(todo => (
                <Grid item xs={12} md={6} lg={4} key={todo.id}>
                  <Card sx={{ cursor: 'pointer', opacity: 0.85 }} onClick={() => { setSelectedTodo(todo); setOpenDetailsDialog(true); }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ textDecoration: 'line-through' }}>
                          {todo.title}
                        </Typography>
                        <Chip label={PRIORITY_CONFIG[todo.priority].label} size="small" color={PRIORITY_CONFIG[todo.priority].color} />
                      </Box>
                      {todo.description && (
                        <Typography variant="body2" color="text.secondary" mb={1} noWrap>{todo.description}</Typography>
                      )}
                      <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                        {todo.category && <Chip label={todo.category} size="small" variant="outlined" />}
                        {todo.completedAt && (
                          <Chip
                            icon={<CalendarIcon sx={{ fontSize: 12 }} />}
                            label={`Completed ${new Date(todo.completedAt).toLocaleDateString('en-GB')}`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(todo.id); }} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {currentTab === 2 && stats && (
        <Box>
          {/* Stats Summary */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Tasks</Typography>
                  <Typography variant="h3" fontWeight={700}>{stats.summary.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Completed</Typography>
                  <Typography variant="h3" fontWeight={700} color="success.main">
                    {stats.summary.completed}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.summary.completionRate}% completion rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>In Progress</Typography>
                  <Typography variant="h3" fontWeight={700} color="primary.main">
                    {stats.summary.inProgress}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Overdue</Typography>
                  <Typography variant="h3" fontWeight={700} color="error.main">
                    {stats.summary.overdue}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Breakdowns */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Priority Breakdown</Typography>
                {stats.priorityBreakdown.map((item, idx) => (
                  <Box key={idx} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography>{PRIORITY_CONFIG[item.priority].icon} {PRIORITY_CONFIG[item.priority].label}</Typography>
                      <Typography fontWeight={600}>{item.count}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(item.count / stats.summary.total) * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                      color={PRIORITY_CONFIG[item.priority].color}
                    />
                  </Box>
                ))}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Category Breakdown</Typography>
                {stats.categoryBreakdown.map((item, idx) => (
                  <Box key={idx} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography>{item.category}</Typography>
                      <Typography fontWeight={600}>{item.count}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(item.count / stats.summary.total) * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
                {stats.categoryBreakdown.length === 0 && (
                  <Typography color="text.secondary">No categorized tasks</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTodo ? '✏️ Edit Task' : '➕ New Task'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Task Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key}>{config.icon} {config.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key}>{config.icon} {config.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Assign To"
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {users.map(usr => (
                  <MenuItem key={usr.id} value={usr.id}>
                    {usr.fullName || usr.username} ({usr.role})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Due Date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Time (minutes)"
                value={formData.estimatedTime}
                onChange={(e) => setFormData({ ...formData, estimatedTime: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">min</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={3}
                placeholder="Additional notes or comments..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.title.trim()}>
            {selectedTodo ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Task Details</Typography>
            <IconButton size="small" onClick={() => setOpenDetailsDialog(false)}>✕</IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTodo && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>{selectedTodo.title}</Typography>
                <Box display="flex" gap={1} mb={2}>
                  <Chip
                    label={STATUS_CONFIG[selectedTodo.status].label}
                    color={STATUS_CONFIG[selectedTodo.status].color}
                    size="small"
                  />
                  <Chip
                    label={PRIORITY_CONFIG[selectedTodo.priority].label}
                    color={PRIORITY_CONFIG[selectedTodo.priority].color}
                    size="small"
                  />
                  {selectedTodo.category && (
                    <Chip label={selectedTodo.category} size="small" variant="outlined" />
                  )}
                </Box>
              </Grid>
              {selectedTodo.description && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{selectedTodo.description}</Typography>
                </Grid>
              )}
              {selectedTodo.dueDate && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Due Date</Typography>
                  <Typography variant="body1" color={isOverdue(selectedTodo.dueDate) ? 'error.main' : 'inherit'}>
                    {formatDate(selectedTodo.dueDate)}
                    {isOverdue(selectedTodo.dueDate) && selectedTodo.status !== 'completed' && ' (Overdue)'}
                  </Typography>
                </Grid>
              )}
              {selectedTodo.estimatedTime && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Estimated Time</Typography>
                  <Typography variant="body1">{selectedTodo.estimatedTime} minutes</Typography>
                </Grid>
              )}
              {selectedTodo.progress > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Progress</Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <LinearProgress variant="determinate" value={selectedTodo.progress} sx={{ flexGrow: 1, height: 8, borderRadius: 4 }} />
                    <Typography variant="body2" fontWeight={600}>{selectedTodo.progress}%</Typography>
                  </Box>
                </Grid>
              )}
              {selectedTodo.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                    <Typography variant="body2">{selectedTodo.notes}</Typography>
                  </Paper>
                </Grid>
              )}
              {selectedTodo.creator && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Created By</Typography>
                  <Typography variant="body2">{selectedTodo.creator.fullName || selectedTodo.creator.username}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(selectedTodo.createdAt).toLocaleString('en-GB')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
          <Button onClick={() => { setOpenDetailsDialog(false); handleOpenDialog(selectedTodo); }} variant="contained">
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TodoApp;
